/**
 * remote-control — Ejecuta en el DOM real las acciones que el agente envía
 * por `agent_control` (click, scroll, escritura, Enter).
 *
 * El gate de consentimiento vive acá: `createRemoteControlHandler` revisa
 * `isEnabled()` en CADA evento entrante, no solo al conectar — así apagar el
 * switch "Permitir control remoto" corta el control al toque, aunque la
 * sesión siga activa.
 */

/** Teclas especiales soportadas — navegación/confirmación, no edición de texto (eso va por 'input'). */
const KEY_CODES: Record<string, string> = {
  Enter: 'Enter',
  Tab: 'Tab',
  Escape: 'Escape',
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
}

export type AgentControlPayload =
  | { kind: 'click' | 'dblclick'; xr: number; yr: number }
  | { kind: 'scroll'; xr: number; yr: number; deltaY: number; deltaX: number }
  | { kind: 'input'; value: string }
  | { kind: 'keydown'; key: keyof typeof KEY_CODES }

function dispatchClick(xr: number, yr: number, dbl: boolean): void {
  const x = xr * window.innerWidth
  const y = yr * window.innerHeight
  const el = document.elementFromPoint(x, y)
  if (!el) return

  const base = { bubbles: true, cancelable: true, view: window, clientX: x, clientY: y }
  // pointerdown/pointerup van como PointerEvent real (no MouseEvent) — componentes
  // basados en Radix (ej. dropdowns/selects custom del header) a veces llaman
  // hasPointerCapture(event.pointerId) al recibir el evento; con un MouseEvent,
  // pointerId queda undefined y esa llamada puede fallar en silencio, dejando el
  // menú sin abrir aunque el resto de la página siga viva.
  const pointerBase = { ...base, pointerId: 1, pointerType: 'mouse', isPrimary: true, button: 0, buttons: 1 }
  el.dispatchEvent(new PointerEvent('pointerdown', pointerBase))
  el.dispatchEvent(new MouseEvent('mousedown', base))
  el.dispatchEvent(new PointerEvent('pointerup', pointerBase))
  el.dispatchEvent(new MouseEvent('mouseup', base))
  el.dispatchEvent(new MouseEvent('click', base))
  if (dbl) el.dispatchEvent(new MouseEvent('dblclick', base))

  if (el instanceof HTMLElement && typeof el.focus === 'function') {
    el.focus()
  }
}

// Mismo límite de "el navegador exige un gesto real" que <select> — un
// <input type="date/time/month/week"> tampoco abre su calendario/reloj
// nativo ante un click sintético. A diferencia de <select>, SÍ se puede
// completar tipeando: el setter nativo de abajo acepta escribir el valor
// directo (ej. "2026-08-17" para type="date") sin necesitar abrir ningún
// picker visual — por eso estos campos se llenan con "modo escritura", no
// clickeando.
function dispatchInput(value: string): void {
  const el = document.activeElement
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return

  // Setter nativo — necesario para que React (inputs controlados) detecte el
  // cambio. Asignar `el.value = value` directo no dispara el listener interno
  // que React engancha sobre el setter del prototipo.
  const proto = el instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  setter?.call(el, value)

  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.dispatchEvent(new Event('change', { bubbles: true }))
}

/**
 * Busca el ancestro scrolleable REAL bajo el punto dado — `window.scrollBy`
 * no alcanza cuando el layout usa un contenedor interno con
 * `overflow-y: auto` (el patrón más común en SPAs con sidebar/header fijo:
 * el body/html no scrollean, un <div> interno sí). Sin esto el scroll remoto
 * simplemente no hacía nada visible en esas pantallas.
 *
 * `axis` decide qué overflow exigir en cada nodo: antes esta función SOLO
 * miraba `overflow-y`, así que una tabla ancha con scroll horizontal propio
 * (`overflow-x: auto`, sin overflow vertical) nunca se detectaba — el scroll
 * horizontal remoto (deltaX, ej. Shift+rueda o gesto de trackpad) siempre
 * caía al fallback `window.scrollBy`, que no mueve nada si el documento no
 * desborda horizontalmente. Si se aceptara cualquier overflow (X u Y) sin
 * distinguir, se rompía el caso inverso: esa misma tabla ancha quedaría
 * marcada como "scrolleable" también para gestos verticales, cortando la
 * búsqueda antes de llegar al contenedor real que sí scrollea en Y.
 */
function findScrollable(el: Element | null, axis: 'x' | 'y'): Element | null {
  let node: Element | null = el
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node)
    if (axis === 'y') {
      const canScrollY = style.overflowY === 'auto' || style.overflowY === 'scroll'
      if (canScrollY && node.scrollHeight > node.clientHeight) return node
    } else {
      const canScrollX = style.overflowX === 'auto' || style.overflowX === 'scroll'
      if (canScrollX && node.scrollWidth > node.clientWidth) return node
    }
    node = node.parentElement
  }
  return null
}

function dispatchScroll(xr: number, yr: number, deltaY: number, deltaX: number): void {
  const x = xr * window.innerWidth
  const y = yr * window.innerHeight
  const point = document.elementFromPoint(x, y)
  // El eje dominante del gesto decide qué tipo de contenedor buscar primero;
  // si no hay ninguno para ese eje, se prueba el otro (ej. un scroll casi
  // puramente horizontal que de casualidad cae sobre un contenedor que solo
  // scrollea en Y) antes de resignarse al fallback de window.
  const primaryAxis: 'x' | 'y' = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y'
  const secondaryAxis: 'x' | 'y' = primaryAxis === 'x' ? 'y' : 'x'
  const scrollable = findScrollable(point, primaryAxis) ?? findScrollable(point, secondaryAxis)
  if (scrollable) {
    scrollable.scrollBy(deltaX, deltaY)
  } else {
    window.scrollBy(deltaX, deltaY)
  }
}

/**
 * <select> nativo: el navegador NUNCA abre el desplegable ante un evento
 * sintético (dispatchEvent produce eventos `isTrusted: false`, y abrir el
 * menú nativo de un <select> — igual que un <input type="date/color/file">
 * — es una acción que los navegadores reservan a gestos REALES del usuario;
 * ningún truco de JS lo puede saltear). Por eso clickear un <select> remoto
 * no mostraba nada.
 *
 * Mitigación: en vez de depender de que el navegador abra el menú, cuando
 * el elemento enfocado es un <select> y llega Arriba/Abajo movemos
 * `selectedIndex` directamente y disparamos `change` — el valor SÍ cambia
 * de forma visible en el <select> cerrado (rrweb lo captura como cualquier
 * otra mutación del DOM), aunque la lista desplegable nunca se vea abierta.
 * El agente navega las opciones con Arriba/Abajo viendo el valor avanzar.
 */
function dispatchSelectArrow(el: HTMLSelectElement, key: 'ArrowUp' | 'ArrowDown'): void {
  const { options } = el
  if (options.length === 0) return
  const dir = key === 'ArrowDown' ? 1 : -1
  const next = Math.min(Math.max(el.selectedIndex + dir, 0), options.length - 1)
  if (next === el.selectedIndex) return
  el.selectedIndex = next
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.dispatchEvent(new Event('change', { bubbles: true }))
}

function dispatchKeydown(key: string): void {
  if (!(key in KEY_CODES)) return
  const el = document.activeElement
  if (!el) return

  if (el instanceof HTMLSelectElement && (key === 'ArrowDown' || key === 'ArrowUp')) {
    dispatchSelectArrow(el, key)
    return
  }

  const opts = { bubbles: true, cancelable: true, key, code: KEY_CODES[key] }
  el.dispatchEvent(new KeyboardEvent('keydown', opts))
  el.dispatchEvent(new KeyboardEvent('keyup', opts))
}

export function applyAgentControl(payload: AgentControlPayload): void {
  switch (payload.kind) {
    case 'click':
      dispatchClick(payload.xr, payload.yr, false)
      break
    case 'dblclick':
      dispatchClick(payload.xr, payload.yr, true)
      break
    case 'scroll':
      dispatchScroll(payload.xr, payload.yr, payload.deltaY, payload.deltaX)
      break
    case 'input':
      dispatchInput(payload.value)
      break
    case 'keydown':
      dispatchKeydown(payload.key)
      break
  }
}

/**
 * Fábrica del handler a pasar como `onAgentControl` en `CobrowseSessionOptions`.
 * Revisa `isEnabled()` en CADA llamada (no solo una vez) — el gate de
 * consentimiento vive acá.
 */
export function createRemoteControlHandler(isEnabled: () => boolean): (payload: unknown) => void {
  return (payload: unknown) => {
    if (!isEnabled()) return
    applyAgentControl(payload as AgentControlPayload)
  }
}
